import { useQuery } from '@tanstack/react-query';
import {
  getUserSignupFormSchema,
  getVerifyExpireLink,
  IOrganizations
} from 'src/api/signUpMutation';
import { SignUpFormFields } from '../signup-form-fields';
import { validateEmail } from 'src/pages/Sign-Up/SignUpForm';
import { IAffiliationDetails } from '@publishing-engine/domain/models';
import { SpecialtyKeywordRow } from './SpecialtyKeywordTable';
import { PhoneFieldData } from 'src/components/FormField/common/PhoneNumberInput';
import { BannerVariant } from '@morressier/alembic/_dist/cjs/components/banners/types';

export type StepOneForm = {
  firstName: string;
  middleName: string;
  lastName: string;
  password: string;
  primaryEmail: string;
  confirmPassword: string;
  prefix: string;
  secondaryEmail?: string;
  phoneNumber?: PhoneFieldData;
  linkedInUrl?: string;
  ccEmail?: string;
  isSameAsSecondaryEmail?: boolean;
  additionalEmails?: Array<{ email: string }>;
  orcid?: string;
  degree?: string;
  isAgreeTermsCondition: boolean;
  isCopyrightAndLicensingAgreement: boolean;
  profileDetails: IProfileDetailsType;
  jobTitle?: string;
};

export type IOrganizationForm = {
  organizationName: string;
  streetAddress: string;
  city: string;
  stateOrRegion: string;
  postalCode: string;
  country: string;
  countryCode: string;
  url?: string;
  ringgoldId?: string;
  selectedAffiliation?: IOrganizations;
  searchValue: string;
  department?: string;
};

export type StepTwoForm = {
  affiliationDetails: IOrganizationForm;
  additionalAffiliations?: IOrganizationForm[];
};

export type OrgField = keyof IOrganizationForm;

export type IOrganizationField = {
  label: string;
  fieldName: OrgField;
  placeholder: string;
  validation: string;
  subText?: string;
};

export const passwordCheckRegX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!$@%])[A-Za-z\d!$@%]{8,}$/;

export const minLimit = 1;
export const maxFirstNameLimit = 50;
export const maxLastNameLimit = 100;
export const minPhoneNumberLimit = 7;
export const maxPhoneNumberLimit = 15;
export const maxPasswordLimit = 64;

export const organizationFields: IOrganizationField[] = [
  {
    label: 'Organization Name',
    fieldName: 'organizationName',
    placeholder: 'e.g., Molecular connections',
    validation: 'Please enter an organization name.'
  },
  {
    label: 'Street Address',
    fieldName: 'streetAddress',
    placeholder: 'e.g., Zossener Str. 55',
    validation: 'Please enter a street address.'
  },
  {
    label: 'City',
    fieldName: 'city',
    placeholder: 'e.g., Berlin',
    validation: 'Please enter a city'
  },
  {
    label: 'State or Region',
    fieldName: 'stateOrRegion',
    placeholder: 'e.g., Brandenburg',
    validation: 'Please enter a state or region.'
  },
  {
    label: 'Postal Code',
    fieldName: 'postalCode',
    placeholder: 'e.g., 10247',
    validation: 'Please enter a postal code.'
  },
  {
    label: 'Country',
    fieldName: 'country',
    placeholder: 'e.g., Germany',
    validation: 'Please enter a country.'
  },
  {
    label: 'URL',
    fieldName: 'url',
    placeholder: 'e.g., https://molecularconnections.com/',
    validation: 'Please enter a URL.',
    subText: 'Provide a direct link to main organization page if possible.'
  }
];

export type IProfileDetailsType = {
  phoneNumber?: PhoneFieldData;
  linkedinProfileUrl?: string;
};

export type IResearchFocusAndExpertiseType = {
  reviewerInterest: string;
  unavailabilityPeriodFrom: string;
  unavailabilityPeriodTo: string;
  scandinavianKeywords?: string[];
  professionalInterests?: string[];
  specialtyAndKeywords: {
    id: string;
    keyword: string;
    keywordId: string;
    specialtyId: string;
    specialtyName: string;
    subSpecialtyId?: string;
    subSpecialtyName?: string;
    scandinavianKeyword?: string;
    scandinavianKeywordId?: string;
  }[];
};

export type IInstitutionalInformationType = {
  additionalAffiliations: Array<string>;
  additionalAffiliationsTemp: { value: string }[];
  careerStage?: string;
  department?: string;
  orcid?: string;
};

export type IAddressType = {
  fullName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  postalCode: string;
  stateOrRegion: string;
};

export type IUserType = {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  affiliation: string;
  ringgoldId: string;
  orcid: string;
  status: string;
  country: string;
  countryCode: string;
  prefix: string;
  degree: string;
  secondaryEmail: string;
  ccEmail: string;
  isSameAsSecondaryEmail: boolean;
  additionalEmails?: string[];
  lastSentEmailStatus?: string;
  lastSentEmailTimeStamp?: number;
  lastLoginAt?: string;
  lastSentStatus?: string;
  lastSentTimeStamp?: string;
  isEmailDelivered?: Boolean;
  secondaryEmailDetails?: {
    email: string;
    isVerified: boolean;
    emailTimeStamp: number;
    isEmailDelivered: boolean;
  };
  ccEmailDetails?: {
    email: string;
    isVerified: boolean;
    emailTimeStamp: number;
    isEmailDelivered: boolean;
  };
  jobTitle: string;
};

export type IUserDetailsStepThreeForm = {
  researchFocusAndExpertise: {
    reviewerInterest: string;
    unavailabilityPeriodFrom: string;
    unavailabilityPeriodTo: string;
    specialtyAndKeywords: SpecialtyKeywordRow[];
    scandinavianKeywords: string[];
    scandinavianKeywordsTemp: Array<{ text: string }>;
    professionalInterests: string[];
    professionalInterestsTemp: Array<{ text: string }>;
  };
};

export const useUserDetailsData = (organizationId: string) => {
  const {
    data: userSignupFormSchema,
    isLoading,
    isSuccess
  } = useQuery({
    queryKey: ['register-user-signup-form', organizationId],
    queryFn: () =>
      organizationId ? getUserSignupFormSchema(organizationId) : null
  });

  const signupFormFields = new SignUpFormFields(
    userSignupFormSchema?.documentSchema
  );
  const configs = {
    FirstName: signupFormFields.getFieldConfig('FirstName'),
    MiddleName: signupFormFields.getFieldConfig('MiddleName'),
    LastName: signupFormFields.getFieldConfig('LastName'),
    ccEmail: signupFormFields.getFieldConfig('ccEmail'),
    Password: signupFormFields.getFieldConfig('Password'),
    ConfirmPassword: signupFormFields.getFieldConfig('ConfirmPassword'),
    PrimaryEmail: signupFormFields.getFieldConfig('PrimaryEmail'),
    SecondaryEmail: signupFormFields.getFieldConfig('SecondaryEmail'),
    Prefix: signupFormFields.getFieldConfig('Prefix'),
    ORCID: signupFormFields.getFieldConfig('ORCID'),
    Degree: signupFormFields.getFieldConfig('ProfessionalDegree'),
    OrganizationName: signupFormFields.getFieldConfig('OrganizationName'),
    StreetAddress: signupFormFields.getFieldConfig('StreetAddress'),
    Country: signupFormFields.getFieldConfig('Country'),
    StateOrRegion: signupFormFields.getFieldConfig('StateOrRegion'),
    City: signupFormFields.getFieldConfig('City'),
    PostalCode: signupFormFields.getFieldConfig('PostalCode'),
    Url: signupFormFields.getFieldConfig('Url'),
    Department: signupFormFields.getFieldConfig('Department'),
    DateOfBirth: signupFormFields.getFieldConfig('DateOfBirth'),
    Gender: signupFormFields.getFieldConfig('Gender'),
    PhoneNumber: signupFormFields.getFieldConfig('PhoneNumber'),
    LinkedinProfileUrl: signupFormFields.getFieldConfig('LinkedinProfileUrl'),
    ResearchInterests: signupFormFields.getFieldConfig('ResearchInterests'),
    AreasOfExpertise: signupFormFields.getFieldConfig('AreasOfExpertise'),
    ReviewerInterest: signupFormFields.getFieldConfig('ReviewerInterest'),
    ScandinavianKeyword: signupFormFields.getFieldConfig('ScandinavianKeyword'),
    UnavailabilityPeriod: signupFormFields.getFieldConfig(
      'UnavailabilityPeriod'
    ),
    UnavailabilityPeriodFrom: signupFormFields.getFieldConfig(
      'UnavailabilityPeriodFrom'
    ),
    UnavailabilityPeriodTo: signupFormFields.getFieldConfig(
      'UnavailabilityPeriodTo'
    ),
    Specialty: signupFormFields.getFieldConfig('Specialty'),
    SubSpecialty: signupFormFields.getFieldConfig('SubSpecialty'),
    Keyword: signupFormFields.getFieldConfig('Keyword'),
    TermsAndConditionAgreement: signupFormFields.getFieldConfig(
      'isAgreeTermsCondition'
    ),
    copyrightAndLicensingAgreement: signupFormFields.getFieldConfig(
      'isCopyrightAndLicensingAgreement'
    ),
    JobTitle: signupFormFields.getFieldConfig('JobTitle'),
    ProfessionalInterests: signupFormFields.getFieldConfig(
      'ProfessionalInterests'
    )
  };

  return {
    configs,
    isLoading,
    isSuccess
  };
};

export function checkIsDataMissing(obj: any): boolean {
  function check(value: any): boolean {
    if (value === null || value === undefined) return true;

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    if (Array.isArray(value)) {
      return value.length === 0 || value.some(item => check(item));
    }

    if (typeof value === 'object') {
      return Object.values(value).some(val => check(val));
    }

    return false;
  }

  return check(obj);
}

export function toTitleCase(sentence: string) {
  return sentence
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, match => match.toUpperCase());
}

export const handleWhiteSpaceValidation = (
  value: string,
  label: string,
  min?: number | undefined,
  max?: number | undefined
) => {
  const isNameField =
    label.toLowerCase().includes('first') ||
    label.toLowerCase().includes('middle') ||
    label.toLowerCase().includes('last');

  // Email validation
  if (label.toLowerCase().includes('email')) {
    if (value.trim().length === 0) {
      return `${label} is required`;
    }
    if (!validateEmail.test(value.trim())) {
      return `${label} format is invalid`;
    }
    return true;
  }

  //Linked in validation
  if (label.toLowerCase().includes('linkedin')) {
    const trimmedValue = value.trim();

    // URL format check (only if not empty after trim)
    const urlRegex = /^(https?:\/\/|www\.).*/i;
    if (trimmedValue.length === 0 || !urlRegex.test(trimmedValue)) {
      return `${label} must start with http://, https://, or www`;
    }

    return true;
  }

  // URL validation (organization website, non-LinkedIn)
  if (label.toLowerCase().includes('url')) {
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) {
      return `${label} cannot be just whitespace`;
    }
    const urlRegex =
      /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(:\d{1,5})?([/?#][^\s]*)?$/;
    if (!urlRegex.test(trimmedValue)) {
      return `${label} is not a valid URL`;
    }
    return true;
  }

  // Phone number validation: only digits, '+' and '-'
  if (label.toLowerCase().includes('phone number')) {
    const phonePattern = /^[0-9+-]*$/;
    if (!phonePattern.test(value)) {
      return `${label} can only contain numbers, '+' and '-' characters`;
    }
    if (min && value.length < min) {
      return `${label} must be at least 7 characters long`;
    }
    if (max && value.length > max) {
      return `${label} must not exceed 15 characters`;
    }
    // if phone number validation passed, do not run other validations
    return true;
  }
  const consecutiveSpecialCharPattern = /([^a-zA-Z0-9\s])\1+/;

  if (
    consecutiveSpecialCharPattern.test(value) &&
    !label.toLowerCase().includes('password') &&
    !label.toLowerCase().includes('url')
  ) {
    return 'No consecutive repeating special characters allowed';
  }

  if (value.trim().length === 0) {
    return `${label} cannot be just whitespace`;
  }

  if (isNameField) {
    if (/\s{2,}/.test(value.trim())) {
      return `${label} cannot contain multiple consecutive spaces`;
    }

    // No numbers allowed
    if (/\d/.test(value.trim())) {
      return `${label} cannot contain numbers`;
    }
    if (min && value.length < min) {
      return `${label} must be at least ${min} characters long`;
    }
    if (max && value.length > max) {
      return `${label} must not exceed ${max} characters`;
    }
  }
  return true;
};

export function extractFields(
  config: Record<string, any>,
  sectionField: string
) {
  const addressFields: Record<string, any> = {};

  Object.entries(config).forEach(([key, value]) => {
    if (
      value?.caseDataFields &&
      typeof value?.caseDataFields?.main === 'string' &&
      value?.caseDataFields?.main?.includes(sectionField)
    ) {
      addressFields[key] = value;
    }
  });

  return addressFields;
}

export const stepTwoFormFields = [
  'city',
  'department',
  'country',
  'countryCode',
  'organizationName',
  'postalCode',
  'stateOrRegion',
  'streetAddress',
  'ringgoldId',
  'url'
] as const;

export type AffiliationKeys = (typeof stepTwoFormFields)[number];

export type FilteredAffiliation = Pick<IOrganizationForm, AffiliationKeys>;

export function pickFields<T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[]
): Pick<T, K> {
  const out = {} as Pick<T, K>;
  keys.forEach(key => {
    if (obj && obj[key] !== undefined) {
      const value = obj[key];
      // Trim if string, preserve other types
      out[key] = (typeof value === 'string' ? value.trim() : value) as T[K];
    }
  });
  return out;
}

export function getDomainFromEmail(email: string) {
  const parts = email.match(/@(.+?)\.com/);
  return parts ? parts[1] : null;
}

export const scrollToError = (errors: any) => {
  const firstErrorField = Object.keys(errors || {})[0];
  if (!firstErrorField) return;

  const errorElement = document.querySelector(
    `[data-error="${firstErrorField}"], [data-field-error="true"], input[name="${firstErrorField}"]`
  ) as HTMLElement | null;

  if (!errorElement) return;

  if (document.activeElement === errorElement) {
    return;
  }

  const rect = errorElement.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const buffer = 40; // a bit bigger, more tolerant

  const isInView = rect.top >= buffer && rect.bottom <= vh - buffer;

  if (isInView) {
    errorElement.focus();
    return;
  }

  errorElement.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  });
  errorElement.focus();
};

export type DuplicateMatch = {
  index: number | 'primary'; // offending other index or primary
};

export const handleDuplicateCheck = (
  selectedAffiliation: IAffiliationDetails,
  fieldPrefix: string, // e.g. "additionalAffiliations.2"
  additionalAffiliations: IAffiliationDetails[],
  affiliationDetails?: IAffiliationDetails
): DuplicateMatch[] => {
  const selectedIndex = Number(fieldPrefix.match(/\d+/)?.[0] ?? -1);

  const {
    organizationName,
    streetAddress,
    city,
    stateOrRegion,
    postalCode,
    country,
    countryCode,
    ringgoldId,
    url,
    department
  } = selectedAffiliation;

  const duplicates: DuplicateMatch[] = [];

  const checkAndCollect = (
    other: IAffiliationDetails,
    index: number | 'primary'
  ) => {
    const normRinggold = ringgoldId?.trim() ?? '';
    const normOtherRinggold = other.ringgoldId?.trim() ?? '';

    const bothHaveRinggold = normRinggold !== '' && normOtherRinggold !== '';
    const bothEmptyRinggold = normRinggold === '' && normOtherRinggold === '';
    const differentRinggold = normRinggold !== normOtherRinggold;

    // Same non-empty ringgoldId
    if (bothHaveRinggold && !differentRinggold) {
      duplicates.push({ index });
      return;
    }

    // Different non-empty OR both empty
    if ((bothHaveRinggold && differentRinggold) || bothEmptyRinggold) {
      const hasDup =
        organizationName &&
        organizationName.trim() === other.organizationName?.trim() &&
        streetAddress === other.streetAddress &&
        city === other.city &&
        postalCode &&
        postalCode === other.postalCode &&
        country &&
        country === other.country &&
        countryCode &&
        countryCode === other.countryCode &&
        (!stateOrRegion && !other.stateOrRegion
          ? true
          : !!stateOrRegion &&
            !!other.stateOrRegion &&
            stateOrRegion === other.stateOrRegion) &&
        (!url && !other.url
          ? true
          : !!url && !!other.url && url === other.url) &&
        (!department && !other.department
          ? true
          : !!department &&
            !!other.department &&
            department === other.department);

      if (!hasDup) return;

      duplicates.push({ index });
      return;
    }
  };

  // Check against other secondary affiliations
  additionalAffiliations.forEach((field, index) => {
    if (index === selectedIndex) return;
    checkAndCollect(field, index);
  });

  // Check against primary affiliation
  if (affiliationDetails) {
    checkAndCollect(affiliationDetails, 'primary');
  }

  return duplicates;
};

export const exactTextCheck = (s: string): string => {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
};

type RowDuplicateFieldKey =
  | 'specialty'
  | 'subSpecialty'
  | 'keyword'
  | 'scandinavianKeyword';

export type RowDuplicateIssue = {
  rowIndex: number;
  value: string;
  fieldKeys: RowDuplicateFieldKey[];
};

const getRowDuplicateFields = (row: SpecialtyKeywordRow) =>
  [
    {
      key: 'specialty' as const,
      value: row.specialty?.name
    },
    {
      key: 'subSpecialty' as const,
      value: row.subSpecialty?.name
    },
    {
      key: 'keyword' as const,
      value: row.keyword?.name
    },
    {
      key: 'scandinavianKeyword' as const,
      value: row.scandinavianKeyword?.name
    }
  ].filter(field => Boolean(field.value?.trim()));

export const findRowLevelDuplicates = (
  specialtyAndKeywords: SpecialtyKeywordRow[]
): {
  hasDuplicates: boolean;
  duplicateRows: RowDuplicateIssue[];
} => {
  const duplicateRows: RowDuplicateIssue[] = [];

  specialtyAndKeywords.forEach((row, index) => {
    const fields = getRowDuplicateFields(row).map(field => ({
      key: field.key,
      value: field.value?.trim() ?? '',
      normalizedValue: exactTextCheck(field.value ?? '')
    }));
    const populatedFields = fields.filter(field => field.normalizedValue);
    const uniqueValues = new Set(
      populatedFields.map(field => field.normalizedValue)
    );

    if (populatedFields.length < 2 || uniqueValues.size !== 1) return;

    duplicateRows.push({
      rowIndex: index + 1,
      value: populatedFields[0]?.value ?? '',
      fieldKeys: populatedFields.map(field => field.key)
    });
  });

  return {
    hasDuplicates: duplicateRows.length > 0,
    duplicateRows
  };
};

export const buildRowDuplicateMessage = (issue: RowDuplicateIssue) => {
  if (issue.fieldKeys.length < 2) {
    return `Row ${issue.rowIndex} contains duplicate values.`;
  }

  return `Duplicate Research Specialty & Keyword combination is not allowed. Please modify at least one field.`;
};

export type IExpireLinkResponse = {
  isExpired: boolean;
  message: string;
  success: boolean;
};
export const useVerifyTokenLink = (token: string) => {
  //Validate link
  const { data, error, isError } = useQuery({
    queryKey: ['verify-expire-link', token],
    queryFn: () => (token ? getVerifyExpireLink(token) : null),
    enabled: !!token
  });

  const toastObject = {
    variant: 'error' as BannerVariant,
    title: 'Link Expired',
    description: 'This link has expired. Please request a new link'
  };

  return {
    data,
    toastObject,
    error,
    isError
  };
};
